import type * as api from '@gitbook/api-client';

/** Prefix of every site setup conversation id. */
const SITE_SETUP_CONVERSATION_PREFIX = 'c-setup-';

/**
 * Each site setup step: its title on its card, what it is about (from the GitBook docs), the action
 * of its card's button, and the choices the app offers, which the agent acts on as described.
 */
export const SITE_SETUP_STEPS: Record<
    `${api.SiteSetupStepId}`,
    {
        title: string;
        /** What the step is about, which the agent introduces it with. */
        about: string;
        /**
         * Skipping the step with its message, or publishing for the last one, from the app's own
         * publish button: never skipped, only done.
         */
        action: { kind: 'skip'; message: string } | { kind: 'publish' };
        /**
         * `description` is shown to the user under its label, `meaning` tells the agent what it
         * asks, and `opens` names what a choice opens instead of sending its label: part of the app,
         * or more cards to pick from.
         */
        choices: ReadonlyArray<{
            label: string;
            description: string;
            meaning: string;
            opens?: 'audience-settings' | 'customization-actions' | 'git-sync';
        }>;
        /**
         * Set on the steps the user may want to keep working on once done: the next one then waits
         * for them to move on, and this describes the card that keeps on with the step instead.
         */
        keepEditing?: string;
    }
> = {
    'first-edit': {
        ...skippableStep('Make a first edit'),
        about: "The first edit shapes the site's content and navigation: import existing docs, add an API reference, or write new pages and sections.",
        keepEditing: 'Keep working on the content and navigation.',
        choices: [
            {
                label: 'Import content',
                description: 'From a URL, or from files you attach.',
                meaning: 'asks you to import content: tell the user to paste a URL or attach files',
            },
            {
                label: 'Import an OpenAPI spec',
                description: 'Generate an API reference from a spec.',
                meaning: 'asks you to add an API reference: ask for the spec URL or file',
            },
            {
                label: 'Add a landing page',
                description: 'A first page that introduces the docs.',
                meaning: 'asks you to suggest a landing page for the site',
            },
            {
                label: 'Add a section',
                description: 'Like an API reference, a changelog or an FAQ.',
                meaning:
                    'asks you to suggest a section, like an API reference, a changelog or an FAQ',
            },
        ],
    },
    customize: {
        ...skippableStep('Customize the site'),
        about: 'Customizing brands the site with its logo, colors, fonts and theme, for the whole site or per section.',
        keepEditing: 'Keep customizing the site.',
        choices: [
            {
                label: "Use my website's logo and colors",
                description: 'Match the docs to your brand.',
                meaning:
                    "asks you to ask for the company's website and suggest its logo and colors",
            },
            {
                label: 'Change the theme',
                description: 'Pick the colors and style of the site.',
                meaning: 'asks you to suggest a theme for the site',
            },
            {
                label: 'Change the layout',
                description: 'Arrange the header and navigation.',
                meaning: 'asks you to suggest a layout for the site',
            },
        ],
    },
    'git-sync': {
        ...skippableStep('Set up Git Sync'),
        about: 'Git Sync connects the site to a GitHub or GitLab repository and keeps both in sync, so the docs can be edited in GitBook or committed from Git.',
        choices: [
            {
                label: 'Set up Git Sync',
                description: 'Connect a GitHub or GitLab repository.',
                // Sent only when the app cannot open the setup itself.
                meaning: 'asks how to connect a repository, from Git Sync in the site sidebar',
                opens: 'git-sync',
            },
        ],
    },
    domain: {
        ...skippableStep('Add a domain'),
        about: 'A custom domain serves the docs at your own address, such as docs.example.com, instead of the default gitbook.io one.',
        choices: [
            {
                label: 'Add a custom domain',
                description: 'Something like docs.example.com. Needs one DNS record.',
                meaning: 'asks you to guide the user to add one',
            },
            {
                label: 'Keep the current address',
                description: 'You can add a domain later from Settings.',
                meaning: 'skips the step and keeps the address the site publishes at',
            },
        ],
    },
    publish: {
        title: 'Publish',
        about: "Publishing puts the site online. Who can see it, publicly, by link or behind a sign-in, is set in the site's Audience settings, before or after publishing.",
        action: { kind: 'publish' },
        choices: [
            {
                label: 'Choose your audience',
                description: 'Control who sees your content once published.',
                meaning:
                    "opens the site's Audience settings, where the user picks who can see the site: public, shared by link, or behind a sign-in",
                opens: 'audience-settings',
            },
            {
                label: 'Check your site customization',
                description:
                    'Add a footer, links to your social accounts, define a social preview image, and more.',
                meaning: 'shows the customization actions below as cards',
                opens: 'customization-actions',
            },
        ],
    },
};

/**
 * Start of the setup conversation ids of a site. Conversations belong to the organization, so the
 * site is in the id: the same id opened on another site is not its setup.
 */
/**
 * The cards the app offers once a step with {@link SITE_SETUP_STEPS}' `keepEditing` is done: each
 * sends its label, and the agent does as described.
 */
export const SITE_SETUP_MOVE_ON_CHOICES = {
    next: {
        label: 'Go to the next step',
        description: 'Continue the setup guide.',
        meaning: 'moves on to the next step',
    },
    keepEditing: {
        label: 'Make more changes',
        meaning: 'keeps working on the step just done',
    },
} as const;

/**
 * The customizations the app offers as cards when the user asks for some before publishing. A card
 * sends its label, and the agent does as described.
 */
export const SITE_CUSTOMIZATION_ACTIONS: ReadonlyArray<{
    label: string;
    description: string;
    meaning: string;
}> = [
    {
        label: "Use my website's logo and colors",
        description: 'Match the docs to your brand.',
        meaning: "ask for the company's website and suggest its logo and colors",
    },
    {
        label: 'Add a logo and favicon',
        description: 'Show your brand in the header and the browser tab.',
        meaning: 'ask for the images, or a website to take them from, and set them',
    },
    {
        label: 'Add footer links',
        description: 'Link to support, privacy, or your social accounts.',
        meaning: 'ask which links to add to the footer, and add them',
    },
    {
        label: 'Set a social preview image',
        description: 'Choose the image shown when your docs are shared.',
        meaning: 'ask for the image and set it',
    },
    {
        label: 'Change the theme',
        description: 'Pick the colors and style of the site.',
        meaning: 'suggest a theme for the site',
    },
    {
        label: 'Change the layout',
        description: 'Arrange the header and navigation.',
        meaning: 'suggest a layout for the site',
    },
];

export function getSiteSetupConversationPrefix(siteId: string): string {
    return `${SITE_SETUP_CONVERSATION_PREFIX}${siteId.replace(/^site_/, '')}-`;
}

/** The site a setup conversation sets up, read from its id. Null for any other conversation. */
export function getSiteSetupConversationSite(conversationId: string): string | null {
    if (!isSiteSetupConversation(conversationId)) {
        return null;
    }
    const [siteShortId] = conversationId.slice(SITE_SETUP_CONVERSATION_PREFIX.length).split('-');
    return siteShortId ? `site_${siteShortId}` : null;
}

/** Whether a conversation is a setup conversation, of the given site when one is given. */
export function isSiteSetupConversation(conversationId: string, siteId?: string): boolean {
    return conversationId.startsWith(
        siteId ? getSiteSetupConversationPrefix(siteId) : SITE_SETUP_CONVERSATION_PREFIX
    );
}

/** The site setup steps a conversation has shown, in order. */
export function getSiteSetupToolCalls(messages: api.AIMessage[]): api.AIToolCallSiteSetupStep[] {
    return messages.flatMap((message) =>
        message.steps.flatMap((step) =>
            (step.toolCalls ?? []).filter(
                (toolCall): toolCall is api.AIToolCallSiteSetupStep =>
                    toolCall.tool === 'siteSetupStep'
            )
        )
    );
}

/**
 * A step with its title, skipped by a message naming it: asked twice, as by a double click, the
 * agent skips it once rather than skipping the step after it too.
 */
function skippableStep(title: string) {
    return { title, action: { kind: 'skip', message: `Skip "${title}"` } } as const;
}
